import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  masterFetchStores,
  masterFetchStoreDetail,
  masterSuspendStore,
  masterActivateStore,
  masterPostAnnouncement,
  logoutMaster,
  getMasterAdmin,
} from '../lib/master-api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;
const formatDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('ko-KR');
};

const STATUS_META = {
  trial:     { label: '체험', color: '#0EA5E9', bg: '#E0F2FE' },
  active:    { label: '활성', color: '#00BF40', bg: '#E8FAE8' },
  past_due:  { label: '미납', color: '#FF9500', bg: '#FFF5E0' },
  cancelled: { label: '해지', color: '#F04452', bg: '#FFF0F0' },
  expired:   { label: '만료', color: '#8B95A1', bg: '#F2F4F6' },
};

const MasterStoresPage = () => {
  const navigate = useNavigate();
  const admin = getMasterAdmin();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [drawer, setDrawer] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [announcementOpen, setAnnouncementOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await masterFetchStores({ q, status, plan });
      setStores(data.stores || []);
    } finally {
      setLoading(false);
    }
  }, [q, status, plan]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (store) => {
    setDrawer(store);
    setDrawerData(null);
    try {
      const d = await masterFetchStoreDetail(store.id);
      setDrawerData(d);
    } catch {
      setDrawerData({ error: true });
    }
  };

  const handleToggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('정말 정지하시겠습니까?')) return;
    await masterSuspendStore(id);
    await load();
    if (drawer?.id === id) openDrawer(drawer);
  };
  const handleActivate = async (id) => {
    await masterActivateStore(id);
    await load();
    if (drawer?.id === id) openDrawer(drawer);
  };

  const exportCsv = () => {
    const headers = ['매장명', '이메일', '플랜', '상태', '가입일', '다음결제일'];
    const rows = stores.map((s) => [
      s.name, s.owner_email, s.plan_name || '-', s.sub_status || '-',
      s.created_at, s.next_billing_at || '-'
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F7F9FC' }}>
      {/* Top bar */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        padding: '14px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36,
            background: 'linear-gradient(135deg, #BF9B30 0%, #806413 100%)',
            borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
              <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
            </svg>
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>MASTER CONSOLE</div>
        </div>
        <nav style={{ display: 'flex', gap: 6 }}>
          {[
            { label: '대시보드', to: '/master' },
            { label: '매장', to: '/master/stores' },
            { label: '구독', to: '/master/subscriptions' },
          ].map((item) => (
            <button
              key={item.to}
              onClick={() => navigate(item.to)}
              style={{
                padding: '8px 14px',
                background: window.location.pathname === item.to ? 'var(--primary-light)' : 'transparent',
                color: window.location.pathname === item.to ? 'var(--primary)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: 8,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{admin?.name || '관리자'}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{admin?.email}</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={logoutMaster}>로그아웃</button>
        </div>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.5px' }}>매장 관리</h1>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.88rem', marginTop: 4 }}>
              전체 {stores.length}개 매장
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {selected.size > 0 && (
              <button className="btn btn-outline" onClick={() => setAnnouncementOpen(true)}>
                선택 {selected.size}개 알림
              </button>
            )}
            <button className="btn btn-outline" onClick={exportCsv}>CSV 내보내기</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <input
            type="text"
            placeholder="매장명 또는 이메일 검색..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="form-input"
            style={{ flex: 1, minWidth: 220 }}
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input" style={{ minWidth: 140 }}>
            <option value="">전체 상태</option>
            <option value="trial">체험</option>
            <option value="active">활성</option>
            <option value="past_due">미납</option>
            <option value="cancelled">해지</option>
          </select>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="form-input" style={{ minWidth: 140 }}>
            <option value="">전체 플랜</option>
            <option value="plan-free">무료 체험</option>
            <option value="plan-basic">베이직</option>
            <option value="plan-pro">프로</option>
            <option value="plan-enterprise">엔터프라이즈</option>
          </select>
          <button className="btn btn-primary" onClick={load}>검색</button>
        </div>

        {/* Table */}
        <div style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 14,
          overflow: 'hidden',
        }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
            </div>
          ) : stores.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-tertiary)' }}>매장이 없습니다</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#FAFBFD', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ ...thStyle, width: 44 }}>
                      <input
                        type="checkbox"
                        checked={selected.size === stores.length && stores.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) setSelected(new Set(stores.map((s) => s.id)));
                          else setSelected(new Set());
                        }}
                      />
                    </th>
                    <th style={thStyle}>매장</th>
                    <th style={thStyle}>플랜</th>
                    <th style={thStyle}>가입일</th>
                    <th style={thStyle}>다음 결제</th>
                    <th style={thStyle}>상태</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((s) => {
                    const meta = STATUS_META[s.sub_status] || STATUS_META.trial;
                    return (
                      <tr
                        key={s.id}
                        style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onClick={() => openDrawer(s)}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#FAFBFD'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(s.id)}
                            onChange={() => handleToggleSelect(s.id)}
                          />
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 700, color: 'var(--text)' }}>{s.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{s.owner_email}</div>
                        </td>
                        <td style={tdStyle}>{s.plan_name || '-'}</td>
                        <td style={tdStyle}>{formatDate(s.created_at)}</td>
                        <td style={tdStyle}>{formatDate(s.next_billing_at)}</td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            background: meta.bg,
                            color: meta.color,
                          }}>
                            {meta.label}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          {s.sub_status === 'active' || s.sub_status === 'trial' ? (
                            <button className="btn btn-outline btn-sm" onClick={() => handleSuspend(s.id)}>정지</button>
                          ) : (
                            <button className="btn btn-outline btn-sm" onClick={() => handleActivate(s.id)}>활성화</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <>
          <div
            onClick={() => setDrawer(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 900,
            }}
          />
          <div style={{
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: '100%',
            maxWidth: 480,
            background: '#fff',
            zIndex: 901,
            boxShadow: '-20px 0 60px rgba(0,0,0,0.2)',
            overflowY: 'auto',
          }}>
            <div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase' }}>
                  매장 상세
                </div>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 800, marginTop: 4 }}>{drawer.name}</h2>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  {drawer.owner_email}
                </div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => setDrawer(null)}>닫기</button>
            </div>

            <div style={{ padding: 24 }}>
              {!drawerData ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div className="spinner spinner-dark" style={{ width: 28, height: 28, borderWidth: 3 }} />
                </div>
              ) : drawerData.error ? (
                <div style={{ color: 'var(--danger)' }}>상세 정보를 불러올 수 없습니다</div>
              ) : (
                <>
                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
                    <MiniStat label="테이블" value={drawerData.stats.tables} />
                    <MiniStat label="메뉴" value={drawerData.stats.menus} />
                    <MiniStat label="누적 매출" value={formatWon(drawerData.stats.total_revenue)} />
                  </div>

                  {/* Subscription */}
                  {drawerData.subscription && (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                        구독 정보
                      </div>
                      <div style={{
                        background: 'var(--bg)',
                        padding: 16,
                        borderRadius: 10,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{drawerData.subscription.plan_name}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                          상태: {drawerData.subscription.status} · {drawerData.subscription.billing_cycle}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                          다음 결제: {formatDate(drawerData.subscription.next_billing_at)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent invoices */}
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>
                      최근 청구서
                    </div>
                    {(drawerData.invoices || []).length === 0 ? (
                      <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>청구서가 없습니다</div>
                    ) : (
                      drawerData.invoices.slice(0, 5).map((i) => (
                        <div key={i.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '10px 0',
                          borderBottom: '1px solid var(--border)',
                          fontSize: '0.85rem',
                        }}>
                          <div>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{i.invoice_number}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{formatDate(i.created_at)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700 }}>{formatWon(i.total)}</div>
                            <div style={{ fontSize: '0.72rem', color: i.status === 'paid' ? 'var(--success)' : 'var(--warning)' }}>
                              {i.status}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Announcement modal */}
      {announcementOpen && (
        <AnnouncementModal
          count={selected.size}
          onClose={() => setAnnouncementOpen(false)}
          onSend={async (payload) => {
            await masterPostAnnouncement(payload);
            setSelected(new Set());
            setAnnouncementOpen(false);
          }}
        />
      )}
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div style={{
    background: 'var(--bg)',
    padding: 12,
    borderRadius: 8,
    textAlign: 'center',
  }}>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginBottom: 2 }}>{label}</div>
    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text)' }}>{value}</div>
  </div>
);

const AnnouncementModal = ({ count, onClose, onSend }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState('info');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !body) return;
    setSending(true);
    try {
      await onSend({ title, body, level, audience: 'all' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 28,
          width: '100%',
          maxWidth: 480,
        }}
      >
        <h3 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: 6 }}>알림 발송</h3>
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginBottom: 20 }}>
          선택된 {count}개 매장에 알림을 발송합니다
        </p>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>레벨</label>
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="form-input" style={{ width: '100%' }}>
            <option value="info">정보</option>
            <option value="warning">주의</option>
            <option value="critical">긴급</option>
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>제목</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input"
            style={{ width: '100%' }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>내용</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="form-input"
            style={{ width: '100%', minHeight: 120, resize: 'vertical' }}
            required
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={sending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? '발송 중...' : '발송'}
          </button>
        </div>
      </form>
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '14px 20px',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const tdStyle = {
  padding: '14px 20px',
  fontSize: '0.85rem',
  color: 'var(--text)',
};
const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  marginBottom: 6,
};

export default MasterStoresPage;
